import { useState, useEffect, useCallback } from "react";
import { getLocalEntries } from "@/store";
import Modal from "@/components/Modal";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDate(isoOrTimestamp) {
  const d = new Date(isoOrTimestamp);
  if (isNaN(d)) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export default function History({ showToast }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [displayCount, setDisplayCount] = useState(20);
  const [selected, setSelected] = useState(null);

  const load = useCallback((q = search) => {
    const all = getLocalEntries();
    const filtered = q
      ? all.filter((e) => e.text.toLowerCase().includes(q.toLowerCase()))
      : all;
    setTotal(filtered.length);
    setEntries(filtered.slice(0, displayCount));
  }, [search, displayCount]);

  useEffect(() => { load(search); }, [search, displayCount, load]);
  useEffect(() => { setDisplayCount(20); }, [search]);

  return (
    <div className="scroll-view scroll-view-padded" style={{ padding: "24px 20px 0" }} data-testid="history-view">
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 24, fontWeight: 400, color: "var(--cream)", marginBottom: 2 }}>
          Your hypha
        </h2>
        <p style={{
          fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
          color: "var(--cream-faint)", letterSpacing: "0.08em",
        }}>
          {total} {total === 1 ? "entry" : "entries"}
        </p>
      </div>

      {/* Search — 16px prevents iOS zoom, 44px tap target */}
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search your entries..."
        data-testid="history-search-input"
        style={{
          width: "100%", height: 44, fontSize: 16,
          background: "var(--teal-glow)",
          border: "1px solid var(--teal-border)",
          borderRadius: 8, padding: "0 14px",
          color: "var(--cream)", outline: "none",
          marginBottom: 18, WebkitAppearance: "none",
        }}
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
      />

      {/* Empty */}
      {entries.length === 0 && (
        <div style={{
          textAlign: "center", padding: "56px 20px",
          fontSize: 16, fontStyle: "italic",
          color: "var(--cream-faint)", lineHeight: 1.7,
        }}>
          {search ? "No entries match that search." : "Your entries will appear here.\nCheck in today to begin."}
        </div>
      )}

      {/* Entry cards — buttons for proper touch feedback */}
      {entries.map((e) => (
        <button
          key={e.id || e.timestamp}
          onClick={() => setSelected(e)}
          data-testid={`entry-card-${e.id}`}
          style={{
            display: "block", width: "100%", textAlign: "left",
            background: "rgba(29,158,117,0.04)",
            border: "1px solid rgba(29,158,117,0.14)",
            borderRadius: 10, padding: "14px 16px",
            marginBottom: 8, cursor: "pointer",
            transition: "border-color 0.18s, background 0.18s",
            WebkitAppearance: "none",
          }}
        >
          <p style={{
            fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
            letterSpacing: "0.12em", color: "var(--teal)",
            textTransform: "uppercase", marginBottom: 7,
          }}>
            {formatDate(e.created_at || e.timestamp)}
          </p>
          <p style={{
            fontSize: 15, fontStyle: "italic",
            color: "var(--cream-dim)", lineHeight: 1.65,
            display: "-webkit-box",
            WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {e.text}
          </p>
        </button>
      ))}

      {/* Load more */}
      {entries.length < total && (
        <button
          onClick={() => setDisplayCount((c) => c + 20)}
          data-testid="load-more-btn"
          style={{
            width: "100%", height: 44,
            background: "none",
            border: "1px solid var(--teal-border)",
            borderRadius: 8,
            fontFamily: "var(--mono)", fontSize: 12, fontWeight: 300,
            color: "var(--cream-dim)", cursor: "pointer",
            marginTop: 4, letterSpacing: "0.06em",
          }}
        >
          Load more ({total - entries.length} remaining)
        </button>
      )}

      <Modal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
